import { createAppState, registerModule, trace, triggerGlobalEvent } from '@ICube/icube-core';

import { BodyLoader } from '@Modules/body-loader';
import { Lazyloader } from '@Modules/lazyloader';
import WebFont from 'webfontloader';
import { defaultBodyLoaderConfig } from '@Configs/body-loader';
import { defaultCartConfig } from '@Configs/cart';
import { defaultComparisonConfig } from '@Configs/comparison';
import { defaultLazyHtmlConfig } from '@Configs/lazy-html';
import { defaultNiceScrollConfig } from '@Configs/nicescroll';
import { defaultScrollerConfig } from '@Configs/scroller';
import { defaultTabsConfig } from '@Configs/tabs';
import { defaultWishlistConfig } from '@Configs/wishlist';
import { detectIE } from '@Utils/detect-ie';
import { hasTouchEvents as detectTouchEvents } from '@Utils/has-touch-events';
import { getDefaultFancyboxConfig } from '@Configs/fancybox';
import globalHydrationSetup from './lazy-hydration/setups/global';
import { hasWebpSupport } from '@Utils/has-webp-support';
import initLazyHydration from '@ICube/lazy-hydration/init';
import initLazyHydrationBySetup from '@ICube/lazy-hydration/init-by-setup';
import lazyHtml from '@Modules/lazy-html';
import { scrollTo } from '@Utils/scroll-to';

export async function boot() {
    window.ICUBE_CONFIG = GLOBAL_CONFIG;

    createAppState('ICube');
    registerModule('BodyLoader', BodyLoader);
    registerModule('lazyHtml', lazyHtml);

    const hasWebp = await hasWebpSupport();
    const hasTouchEvents = detectTouchEvents();
    const isIE = detectIE();

    defaultScrollerConfig.hasTouchEvents = hasTouchEvents;
    ICube.data.add({
        hasWebp,
        hasTouchEvents,
        isIE,
        lazyLoadHtmlIndex: 0,
        defaultLazyHtmlConfig,
        defaultNiceScrollConfig,
        defaultScrollerConfig,
        defaultWishlistConfig,
        defaultComparisonConfig,
        defaultTabsConfig,
        defaultFancyboxConfig: getDefaultFancyboxConfig(),
        defaultBodyLoaderConfig
    });

    if ('scrollBehavior' in document.documentElement.style === false) {
        await import('smoothscroll-polyfill').then((smoothscroll) => {
            smoothscroll.polyfill();
        });
    }

    initLazyHydration();
    initLazyHydrationBySetup(globalHydrationSetup);

    const $wishlist = $('.js-wishlist');
    const $addToCompare = $('.js-add-to-compare');
    const $addToCart = $('.js-add-to-cart');
    const $lazyHtml = document.querySelectorAll('.js-lazy-html');

    Lazyloader.addClassesOnImagesTags();
    Lazyloader.init();

    if (isIE || !ICUBE_CONFIG.fonts) {
        triggerGlobalEvent('fontactive');
    } else {
        WebFont.load({
            custom: {
                families: ICUBE_CONFIG.fonts
            },
            active: () => {
                triggerGlobalEvent('fontactive');
            }
        });
    }

    if (!hasTouchEvents) {
        trace('.js-nicescroll', function(elements) {
            const $elements = $(elements);

            if ($elements.length) {
                import(PLUGINS_PATH + 'nicescroll/jquery.nicescroll').then(() => {
                    $elements.each(function() {
                        const $self = $(this);

                        if ($self.data('nicescroll-shadow')) {
                            $self.closest($self.data('nicescroll-shadow')).addClass('has-shadow');
                        }

                        const niceScrollInstance = $self.niceScroll(defaultNiceScrollConfig);

                        const resizeOnHover = $self.data('resize-on-hover');
                        if (resizeOnHover) {
                            const $resizeTrigger = resizeOnHover === 'self' ? $self : $self.closest(resizeOnHover);

                            $resizeTrigger.on('mouseenter mouseleave', function() {
                                niceScrollInstance.resize();
                            });
                        }
                    });
                });
            }
        }, null, true);
    }

    $(document).on('click', '.js-scroll-to', scrollTo);

    if ($wishlist.length) {
        import(MODULES_PATH + 'products-storage').then(({ ProductsStorage }) => ProductsStorage.init.call($wishlist, defaultWishlistConfig));
    }

    if ($addToCompare.length) {
        import(MODULES_PATH + 'products-storage').then(({ ProductsStorage }) => ProductsStorage.init.call($addToCompare, defaultComparisonConfig));
    }

    if ($addToCart.length) {
        import(MODULES_PATH + 'cart').then(({ Cart }) => Cart.add.call($addToCart, defaultCartConfig));
    }

    if ($lazyHtml.length) {
        lazyHtml($lazyHtml, Object.assign({}, defaultLazyHtmlConfig));
    }

    if (hasTouchEvents) {
        const $clickables = document.querySelectorAll('.js-has-clickables, .js-is-clickable');

        if ($clickables.length) {
            for (let i = 0, len = $clickables.length; i < len; i++) {
                $clickables[i].classList.remove('js-has-clickables', 'js-is-clickable');
            }
        }
    }

    /* opóźnione ładowanie zewnętrznych skryptów */
    if (ICUBE_CONFIG.use_delayed_scripts) {
        const $delayedScripts = document.querySelectorAll('fscript');

        ICube.delayedScripts = ICube.delayedScripts || [];

        if ($delayedScripts.length) {
            ICube.delayedScripts.push($delayedScripts);
        }

        if (ICube.delayedScripts.length) {
            import(LIBS_PATH + 'icube/delayed-scripts').then(({ delayedScripts }) => delayedScripts(ICube.delayedScripts));
        }
    }
}